import React, { useState, useEffect, useMemo } from "react";
import { Table, Form, FormGroup, Label, Input, Button, Row, Col, Card, CardBody, CardTitle, UncontrolledTooltip } from "reactstrap";
import DatePicker from "react-datepicker";
import Select from 'react-select';
import { useFormik } from "formik";
import * as Yup from "yup";
import "react-datepicker/dist/react-datepicker.css";
import TableContainer from "components/Common/TableContainer";
import { FaPlusCircle } from "react-icons/fa";
import { FiDownload, FiEye, FiPlusCircle } from "react-icons/fi";
import nv3 from "../../../assets/images/brands/b-nav-3.svg";
import {
  createEmployee,
  deleteDocumentRequest,
  fetchEmployeeTabWiseInfo
} from "store/actions";
import { useDispatch, useSelector } from "react-redux";
import { handleDownload } from "common/fileDownload";
import { Link } from "@mui/material";
import Swal from "sweetalert2";

const documentsList = [
  {
    serialNo: 1,
    documentType: "Passport",
    description: "Scanned copy of my passport",
    file: new File([""], "passport.pdf"),
  },
  {
    serialNo: 2,
    documentType: "ID Card",
    description: "National ID proof",
    file: new File([""], "id_card.png"),
  },
  {
    serialNo: 3,
    documentType: "Driver's License",
    description: "Scanned copy of my driver's license",
    file: new File([""], "license.jpg"),
  },
  {
    serialNo: 4,
    documentType: "Employment Contract",
    description: "Signed employment agreement",
    file: new File([""], "contract.pdf"),
  },
];

const EmployeeDocuments = ({ empID, authDetails, dropdownData,isActive }) => {
  const dispatch = useDispatch();
  const {
    employeeDocumentInfo = [],
    success,
    loading,
    error,
  } = useSelector((state) => ({
    employeeDocumentInfo: empID == 0 ? [] : state.EmployeeDetailsReducer?.employeeTabData?.document || [],
    loading: state.employeeReducer?.loading,
    error: state.employeeReducer?.error,
    success: state.EmployeeDetailsReducer?.success
  }));
  

  const initialDocumentList = [
    { documentType: '', description: '', file: null },
  ];

  const [documentList, setDocumentList] = useState(initialDocumentList);
  const { document_type} = dropdownData;

  // Handle adding a new document row
  const handleAddDocumentRow = () => {
    setDocumentList([
      ...documentList,
      { documentType: '', description: '', file: null },
    ]);
  };

  // Handle removing a document row
  const handleRemoveDocumentRow = (index) => {
    const newDocumentList = documentList.filter((_, i) => i !== index);
    setDocumentList(newDocumentList);
    onChange(newDocumentList); // Only pass the updated document list after removal
  };

  // Handle changes in document fields
  const handleDocumentChange = (field, value, index) => {
    const newDocumentList = [...documentList];
    newDocumentList[index][field] = value;
    setDocumentList(newDocumentList);
    onChange(newDocumentList[index]); // Pass only the updated document to the parent
  };

  // Handle file uploads for documents
  const handleFileUpload = (e, index) => {
    const file = e.target.files[0];
    const newDocumentList = [...documentList];
    newDocumentList[index].file = file;
    setDocumentList(newDocumentList);
  };

  const formik = useFormik({
    initialValues: {
      documentType: "",
      description: "",
      file: null,
    },
    validationSchema: Yup.object({
      documentType: Yup.object().required("Document Type is required"),
      description: Yup.string()
        .required("Description is required")
        .max(100, "Description must not exceed 100 characters")
        .matches(
          /^[a-zA-Z0-9\s.,!@#$%^&*()_\-+=:;'"?/\\[\]{}|<>~`]+$/,
          "Description contains invalid characters"
        ),
      file: Yup.mixed()
        .test("fileRequired", "File is required", (value) => value !== null)
        .test("fileSize", "File size is too large (max 2MB)", (value) =>
          value ? value.size <= 2 * 1024 * 1024 : true
        )
        .test(
          'fileType',
          'Only JPG, JPEG, PNG, and PDF formats are allowed',
          (value) =>
            value &&
            ['image/jpg', 'image/jpeg', 'image/png', 'application/pdf'].includes(value.type)
        ),
    }),
    onSubmit: (values) => {
      const payload = {
            doc: [
              {
                emp_id: empID,
                document_type: values.documentType?.value,
                document_name: values.documentType?.label,
                doc_description: values.description,
                doc_filename:""
              }
            ]
        };
      const formData = new FormData();
      formData.append("lst_employee_documents", JSON.stringify(payload));
      formData.append("create_by", authDetails?.user_id);
      formData.append("document_file", values.file);
      dispatch(createEmployee(formData,'document'));
      formik.resetForm()
    },
  });

  // useEffect(() => {
  //   if (formikRef) {
  //     formikRef.current = formik;
  //   }
  // }, [formik, formikRef]);

  // select custome style
  const SelectcustomStyles = {
    control: (base) => ({
      ...base,
      maxheight: 33.1,
      minHeight: 33.1,
      // overflow: "scroll",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      padding: "0", // Match the padding for both dropdown and clear indicator
    }),
    dropdownIndicator: (base) => ({
      ...base,
      padding: "2px 8px", // Ensure consistent padding for dropdown indicator
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: "2px 8px", // Adjust padding for clear indicator to match dropdown
    }),
  };

  const handleToggleStatus = (row) => {
        Swal.fire({
          title: 'Are you sure?',
          html: `You are about to delete ${row.document_type}`,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Confirm',
          cancelButtonText: 'Cancel',
          reverseButtons: false,
          // dangerMode: true,
        }).then((result) => {
          if (result.isConfirmed) {
              const formData = new FormData();
              formData.append("emp_doc_id", row.emp_doc_id);
              formData.append("createdby", authDetails?.user_id);
              dispatch(deleteDocumentRequest(formData));
          }    
        });
      };  

  const columns = useMemo(
    () => [
      {
        Header: "Action",
        Cell: ({ row }) => {
          const fileUrl = row.original.doc_filepath; // Assuming 'file' holds the URL or path
          const filePath = `${process.env.REACT_APP_IMAGE_BASE_URL}${fileUrl}`;
          const fileName = fileUrl.split("/").pop();
          return fileUrl ? (
            <div className="flex gap-2">
              <a href={filePath} target="_blank" rel="noopener noreferrer">
                <FiEye className="text-blue-500 cursor-pointer me-3" title="View" />
              </a>
              <a href={filePath} download={fileName} onClick={(e) => { 
                  e.preventDefault(); // Prevent default behavior
                  handleDownload(filePath, fileName);
                }}>
                <FiDownload className="text-green-500 cursor-pointer me-3" title="Download" />
              </a>
              <Link
                to="#"
                className="text-danger"
                onClick={() => {
                  handleToggleStatus(row.original);
                }}
              >
                <i className="mdi mdi-delete font-size-18" id="deletetooltip" />
                <UncontrolledTooltip placement="top" target="deletetooltip">
                  Delete
                </UncontrolledTooltip>
              </Link>
            </div>
          ) : (
            ""
          );
        },
      },
      { Header: "S.No", Cell: ({ row }) => employeeDocumentInfo.length - row.index , className: "serial-no", applyHeaderClass: true},
      { Header: "Document Type", accessor: "document_type" },
      { Header: "Description", accessor: "doc_description" },
      // { Header: "File", accessor: "file" },
      
    ],
    [employeeDocumentInfo]
  );

  useEffect(() => {
    if(isActive){
      dispatch(fetchEmployeeTabWiseInfo(empID,'document'));
    }
  }, [dispatch,isActive]);

  const [fileKey, setFileKey] = useState(Date.now());
  useEffect(() => {
    if (success) {
      formik.setFieldValue("file", null);

      // regenerate key — browser clears file input
      setFileKey(Date.now());
    }
  }, [success]);


  return (


    <>
      <Card>
        <div className="card-main-title">
          <h6 className="title">
            <img src={nv3} className="img-fluid w-auto h-auto" alt="img" />
            Documents
          </h6>
        </div>
        <CardBody>
          <Form onSubmit={formik.handleSubmit}>
            <Row className="align-items-end">
              {/* Document Type */}
              <Col md={4}>
                <FormGroup>
                  <Label for="documentType">Document Type <span className="text-danger">*</span></Label>
                  <Select
                    styles={SelectcustomStyles}
                    id="documentType"
                    name="documentType"
                    // value={document_type.find(option => option.value === formik.values.documentType) || ""}
                    // onChange={(selectedOption) => formik.setFieldValue("documentType", selectedOption.value)}
                    // options={documentTypeOptions}
                    // onBlur={() => formik.setFieldTouched("documentType")}
                    options={(document_type || []).map(bg => ({ value: bg.doctype_id.toString(), label: bg.doc_type }))}
                    value={formik.values.documentType || null} // Store full object
                    onChange={(selectedOption) => formik.setFieldValue("documentType", selectedOption)}
                    onBlur={formik.handleBlur}
                    className="hrms-select"
                    classNamePrefix="hrms-select"
                  />
                  {formik.touched.documentType && formik.errors.documentType && (
                    <div className="text-danger">{formik.errors.documentType}</div>
                  )}
                </FormGroup>
              </Col>

              {/* Description */}
              <Col md={4}>
                <FormGroup>
                  <Label for="description">Description <span className="text-danger">*</span></Label>
                  <Input
                    type="text"
                    id="description"
                    name="description"
                    maxLength={100}
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.description && formik.errors.description && (
                    <div className="text-danger">{formik.errors.description}</div>
                  )}
                </FormGroup>
              </Col>

              {/* Upload File */}
              <Col md={4}>
                <FormGroup>
                  <Label>Upload File <span className="text-danger">*</span></Label>
                  <div className="d-flex align-items-center">
                    <Input
                      key={fileKey}
                      type="file"
                      id="document-upload"
                      onChange={(e) => formik.setFieldValue("file", e.currentTarget.files[0])}
                      onBlur={() => formik.setFieldTouched("file")}
                    />
                  </div>
                  {formik.touched.file && formik.errors.file && (
                    <div className="text-danger">{formik.errors.file}</div>
                  )}
                </FormGroup>
              </Col>
              <div className="text-start d-flex justify-content-end mt-3">
                <Button  type="submit" className="d-flex align-items-center btn button-add">
                   <i className="fas fa-plus me-2" /> Add
                </Button>
              </div>
            </Row>
          </Form>
        </CardBody>
      </Card>
      <Card>
        <CardBody>
          <Row>
            <Col md={12}>
              <TableContainer
                columns={columns}
                data={employeeDocumentInfo.length > 0 ? employeeDocumentInfo : []}
                isGlobalFilter={true}
                customPageSize={10}
              />
            </Col>
          </Row>
        </CardBody>
      </Card>
    </>
  );
};

export default EmployeeDocuments;
